// src/App.tsx (FULL — email text replaced by user icon w/ tooltip)
// + ✅ Affektra simple polling (outputs-only) via POST /sessions/{session_id}/agent/outputs
// + ✅ FIX: clear_outputs calls POST /sessions/{session_id}/agent/outputs/clear (REAL delete)
// + ✅ FIX: when saving from "current", promote minted sid into SessionSelector (no jump back to current)
// + ✅ FIX: session toggle actually hides/shows the right rail

import React, { useCallback, useEffect, useRef, useState } from "react";
import { trimTrailingSlash } from "./lib/url";

// Features
import Prompts from "./features/prompts";
import SessionPanel from "./features/session/SessionPanel";
import { parseTranscriptToEvents as parseNdjsonTranscript } from "./features/session/lib/transcriptParser";
import { isSessionDirty, clearSessionDirty } from "./features/session/lib/sessionDirty";
import ConfirmDialog from "./components/ConfirmDialog";
import { PasskeysSection } from "./components/PasskeysSection";
import { NotesToReview } from "./components/NotesToReview";
import type { AutoNoteFormat } from "./hooks/usePreferences";
import { ToolkitPage } from "./features/toolkit/ToolkitPage";
import AssessmentsPanel from "./features/assessments";
import GoalsBoard from "./features/goals/GoalsBoard";
import LibraryPanel from "./features/library";
import CalendarPage from "./features/calendar/CalendarPage";
import { StoryPanel } from "./features/histories/StoryPanel";

// Components
import PatientDashboard from "./components/PatientDashboard";
import PreparePanel from "./components/PreparePanel";
import PatientSelector from "./components/PatientSelector";
import SessionSelector from "./components/SessionSelector";
import AssistantPanel from "./components/AssistantPanel";
import PatientsPanel from "./components/PatientsPanel";
import RecordingStatusStrip from "./components/RecordingStatusStrip";
import SubscriptionSection from "./components/SubscriptionSection";
import { useToast } from "./components/Toast";

import {
  type AgentCard,
  type AffektraActionCtx,
  type AffektraRunStatus,
  type CardFeedbackCtx,
} from "./features/session/parts/AffektraPortal";
import { API_BASE } from "./config";
import {
  setPaymentRequiredHandler,
  reportPlanRequiredRunError,
  type PaymentRequiredKey,
} from "./lib/api-client";

import { useAuth } from "./auth/AuthProvider";
import { logout } from "./auth/client";
import AuthCallback from "./pages/AuthCallback";
import { LoginPage } from "./pages/LoginPage";
import OuraConsent from "./pages/OuraConsent";
import PublicFillPage from "./features/publicFill/PublicFillPage";

import { SessionNote } from "./types";
import { usePreferences, type TabId } from "./hooks/usePreferences";
import { useBillingReturn } from "./hooks/useBillingReturn";
import "./App.css";
import "./i18n";
import { useTranslation } from "react-i18next";
import { LANGUAGES, setLanguage, getCurrentLanguage } from "./i18n";

/* ------------------ Config helper ------------------ */
const getApiBaseUrl = (): string => {
  // @ts-ignore – runtime field injected by public/config.js
  const fromRuntime = (window as any).__APP_CONFIG__?.apiBase as string | undefined;
  const fromEnv = (process.env.REACT_APP_API_BASE_URL as string | undefined) || "";
  const chosen = fromRuntime || fromEnv || "";
  if (!chosen) {
    console.warn("[App] apiBaseUrl is empty. Set window.__APP_CONFIG__.apiBase or REACT_APP_API_BASE_URL.");
  }
  return chosen;
};

/* ------------------ Icons (unchanged) ------------------ */
type SvgProps = React.SVGProps<SVGSVGElement>;
const SvgIcon: React.FC<SvgProps> = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" {...p} />
);

const IconToolbox = (p: SvgProps) => (
  <SvgIcon {...p}>
    <rect x="3" y="8" width="18" height="10" rx="2" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    <path d="M3 12h18" />
  </SvgIcon>
);

const IconRobot = (p: SvgProps) => (
  <SvgIcon {...p}>
    <rect x="6" y="9" width="12" height="8" rx="2" />
    <circle cx="9" cy="13" r="1" />
    <circle cx="15" cy="13" r="1" />
    <path d="M12 9V6" />
    <circle cx="12" cy="4" r="1" />
    <path d="M8 17v2M16 17v2" />
  </SvgIcon>
);

const IconLayers = (p: SvgProps) => (
  <SvgIcon {...p}>
    <rect x="7" y="7" width="10" height="10" rx="2" />
    <rect x="3" y="3" width="10" height="10" rx="2" />
  </SvgIcon>
);

const IconBookOpen = (p: SvgProps) => (
  <SvgIcon {...p}>
    <path d="M12 5v14" />
    <path d="M12 5H7.5A3.5 3.5 0 0 0 4 8.5V19h5.5A2.5 2.5 0 0 1 12 21" />
    <path d="M12 5h4.5A3.5 3.5 0 0 1 20 8.5V19h-5.5A2.5 2.5 0 0 0 12 21" />
  </SvgIcon>
);

const IconLibrary = (p: SvgProps) => (
  <SvgIcon {...p}>
    <path d="M4 4v16" />
    <path d="M8 8v12" />
    <path d="M12 6v14" />
    <path d="m16 6 4 14" />
  </SvgIcon>
);

const IconClipboardList = (p: SvgProps) => (
  <SvgIcon {...p}>
    <rect x="6" y="7" width="12" height="13" rx="2" />
    <path d="M9 4h6" />
    <path d="M8 7V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1" />
    <path d="M9 12h6M9 16h6" />
  </SvgIcon>
);

const IconChecklist = (p: SvgProps) => (
  <SvgIcon {...p}>
    <path d="M4 7h6M4 12h6M4 17h6" />
    <path d="M14 6l2 2 4-4" />
    <path d="M14 11l2 2 4-4" />
    <path d="M14 16l2 2 4-4" />
  </SvgIcon>
);

const IconCalendar = (p: SvgProps) => (
  <SvgIcon {...p}>
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M4 9h16" />
    <path d="M8 3v4M16 3v4" />
    <path d="M8 13h2M14 13h2M8 17h2" />
  </SvgIcon>
);

const IconNetwork = (p: SvgProps) => (
  <SvgIcon {...p}>
    <circle cx="6" cy="12" r="2" />
    <circle cx="18" cy="6" r="2" />
    <circle cx="18" cy="18" r="2" />
    <path d="M8 12h6" />
    <path d="M12 10l4.2-2.2" />
    <path d="M12 14l4.2 2.2" />
  </SvgIcon>
);

const IconBeaker = (p: SvgProps) => (
  <SvgIcon {...p}>
    <path d="M8 3v5l-3.5 7.5A3 3 0 0 0 7.3 20h9.4a3 3 0 0 0 2.8-4.5L16 8V3" />
    <path d="M6 3h12M8 9h8M10 13h4" />
  </SvgIcon>
);

const IconUser = (p: SvgProps) => (
  <SvgIcon {...p}>
    <circle cx="12" cy="8" r="3" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
  </SvgIcon>
);

const IconLogout = (p: SvgProps) => (
  <SvgIcon {...p}>
    <path d="M14 8l4 4-4 4" />
    <path d="M3 12h15" />
    <path d="M9 16v1a3 3 0 0 0 3 3h3" />
  </SvgIcon>
);

const IconPanelRight = (p: SvgProps) => (
  <SvgIcon {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M15 4v16" />
  </SvgIcon>
);

/* ------------------ App ------------------ */
const App: React.FC = () => {
  const { isAuthed, ensureToken, user } = useAuth();
  const [checking, setChecking] = useState(true);
  const isCallbackRoute = window.location.pathname.startsWith("/_auth/callback");
  const isOuraRoute = window.location.pathname.startsWith("/_oura/");
  // Public, unauthenticated questionnaire fill page — must render for a
  // logged-out browser, BEFORE the Cognito auth gate (like /_auth and /_oura).
  // Matches the incoming `/q/{token}` link and the scrubbed `/q` the page
  // rewrites to (so a re-render after the token-strip still stays public).
  const isPublicFillRoute =
    window.location.pathname === "/q" || window.location.pathname.startsWith("/q/");

  // Themes offered: "reading-room" (default), "lavender", "ink-night" (dark).
  // Apply the saved choice on load; with no explicit choice, follow the OS scheme.
  useEffect(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem("AFFEKTRA_THEME");
      localStorage.removeItem("SESSION_PANEL_WIDTH");
    } catch {}
    document.documentElement.setAttribute("data-theme", resolveTheme(saved));

    // Track OS scheme changes only while the user hasn't picked a theme explicitly.
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = () => {
      let explicit: string | null = null;
      try {
        explicit = localStorage.getItem("AFFEKTRA_THEME");
      } catch {}
      if (!explicit) document.documentElement.setAttribute("data-theme", resolveTheme(null));
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Stash ?tier=alap|pro (from the landing pricing page) BEFORE any auth flow
  // so it survives the Cognito OAuth roundtrip. Consumed post-login in
  // AuthedApp, which opens Settings → Subscription.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tier = params.get("tier");
    if (tier === "alap" || tier === "pro") {
      try {
        localStorage.setItem("affektra.pendingTier", tier);
      } catch (e) {
        console.error("[App] failed to stash pending tier", e);
      }
    }
  }, []);

  useEffect(() => {
    if (isCallbackRoute || isOuraRoute || isPublicFillRoute) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await ensureToken();
      } catch {
        // token unavailable — will redirect to login
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallbackRoute, isOuraRoute, isPublicFillRoute]);

  // Delegate to ensureToken so every call gets a fresh (or refreshed) token
  const getToken = useCallback(async () => (await ensureToken()) || "", [ensureToken]);

  if (isCallbackRoute) return <AuthCallback />;
  if (isOuraRoute) return <OuraConsent />;
  if (isPublicFillRoute) return <PublicFillPage />;

  if (checking) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div>Loading…</div>
      </div>
    );
  }

  if (!isAuthed) {
    return <LoginPage onAuthenticated={() => window.location.reload()} />;
  }

  return (
    <AuthedApp
      userEmail={user?.email || (user as any)?.username}
      onLogout={logout}
      apiBaseUrl={getApiBaseUrl()}
      getToken={getToken}
    />
  );
};

export default App;

/* ------------------ Child: main app ------------------ */

type UpdateEvent = {
  seq: number;
  ts?: number;
  start_sec?: number;
  end_sec?: number;
  speaker?: string;
  text?: string;
};

const ALLOWED_THEMES = ["reading-room", "lavender", "ink-night"];

/** Explicit saved choice wins; otherwise follow the OS color scheme (ink-night for dark). */
function resolveTheme(saved: string | null): string {
  if (saved && ALLOWED_THEMES.includes(saved)) return saved;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "ink-night" : "reading-room";
}

type Tab = "patients" | "overview" | "similar" | "tech" | "prompts" | "assistant" | "assessments" | "goals" | "prepare" | "library" | "calendar";

const ALL_TABS: Tab[] = ["patients", "overview", "similar", "tech", "prompts", "assistant", "assessments", "goals", "prepare", "library", "calendar"];

// prefId undefined → the item is core chrome: always visible, not hideable via prefs.
type NavItem = { tab: Tab; prefId?: TabId; icon: React.FC<SvgProps>; labelKey: string; group: number };

// Groups follow the therapist's mental model: caseload → today's session → this patient → reference.
const NAV_GROUP_LABELS = ["nav.groupCaseload", "nav.groupSession", "nav.groupPatient", "nav.groupLibrary"];

const NAV_ITEMS: NavItem[] = [
  // "patients" and "calendar" are reached from the user dropdown, not the rail.
  { tab: "prepare", prefId: "prepare", icon: IconChecklist, labelKey: "tabs.prepare", group: 1 },
  { tab: "tech", prefId: "canvas", icon: IconToolbox, labelKey: "tabs.canvas", group: 1 },
  { tab: "assistant", prefId: "assistant", icon: IconRobot, labelKey: "tabs.assistant", group: 1 },
  { tab: "overview", prefId: "journey", icon: IconClipboardList, labelKey: "tabs.journey", group: 2 },
  { tab: "goals", prefId: "goals", icon: IconLayers, labelKey: "tabs.goals", group: 2 },
  { tab: "assessments", prefId: "assessments", icon: IconBeaker, labelKey: "tabs.assessments", group: 2 },
  // Histories compares THIS patient's sessions to similar ones — it belongs to
  // the per-client group, not the reference library.
  { tab: "similar", prefId: "histories", icon: IconNetwork, labelKey: "tabs.histories", group: 2 },
  { tab: "prompts", prefId: "prompts", icon: IconBookOpen, labelKey: "tabs.cards", group: 3 },
  // Library reads as a reference collection (bookshelf), distinct from the
  // single open-book glyph used for Prompts.
  { tab: "library", prefId: "library", icon: IconLibrary, labelKey: "tabs.library", group: 3 },
];

const SelectPatientEmptyState: React.FC<{ title?: string; hint?: string }> = ({
  title = "Select a patient",
  hint = "Choose a patient from the dropdown above to view this section.",
}) => {
  return (
    <div style={{ padding: 18, border: "1px solid var(--color-border)", borderRadius: 12, background: "var(--color-surface)", color: "var(--color-ink)", maxWidth: 720 }}>
      <div style={{ fontSize: 16, fontWeight: 650, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 14, color: "var(--color-muted)" }}>{hint}</div>
    </div>
  );
};

const AuthedApp: React.FC<{
  userEmail?: string;
  onLogout: () => void;
  apiBaseUrl: string;
  getToken: () => Promise<string>;
}> = ({ userEmail, onLogout, apiBaseUrl, getToken }) => {
  const { t } = useTranslation();
  const { ensureIdToken } = useAuth();
  const { prefs, hideTab, toggleTab, setSummarizePrompt, updatePrefs } = usePreferences();
  const toast = useToast();

  // Nav items visible under current prefs, in NAV_ITEMS order.
  const visibleNav = NAV_ITEMS.filter(
    (ni) => !ni.prefId || prefs.visibleTabs.includes(ni.prefId)
  );

  // Global 402 (entitlement gate) toast for the raw-fetch call sites that
  // bypass useApi/ApiClient (which get on402 wired per-instance in useApi).
  useEffect(() => {
    setPaymentRequiredHandler((key: PaymentRequiredKey) => toast.error(t(`settings.${key}`)));
    return () => setPaymentRequiredHandler(null);
  }, [toast, t]);

  // Context menu state for nav items
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; tabId: TabId } | null>(null);

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [ctxMenu]);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const fallback: Tab = "prepare";
    try {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const fromHash = params.get("tab");
      if (fromHash && (ALL_TABS as string[]).includes(fromHash)) return fromHash as Tab;
      const raw = localStorage.getItem("TJ_ACTIVE_TAB");
      if (!raw) return fallback;
      return (ALL_TABS as string[]).includes(raw) ? (raw as Tab) : fallback;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("TJ_ACTIVE_TAB", activeTab);
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.hash.replace(/^#/, ""));
      params.set("tab", activeTab);
      if (activeTab !== "tech") params.delete("item"); // item= belongs to the Toolkit
      url.hash = params.toString();
      window.history.replaceState(null, "", url.toString());
    } catch {}
  }, [activeTab]);

  const [patient, setPatient] = useState<string>("");
  const [patientName, setPatientName] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("current");
  const [effectiveSessionId, setEffectiveSessionId] = useState<string | undefined>(undefined);
  const [storyHighlight, setStoryHighlight] = useState<{ sessionId: string; startMs: number; endMs: number } | null>(null);

  useEffect(() => {
    setSessionId("current");
    setEffectiveSessionId(undefined);
  }, [patient]);

  const [sessionNote, setSessionNote] = useState<SessionNote>({
    patient: "",
    summary: "",
  });

  // Ref to access sessionNote in callbacks without stale closure issues
  const sessionNoteRef = useRef<SessionNote>(sessionNote);
  useEffect(() => {
    sessionNoteRef.current = sessionNote;
  }, [sessionNote]);

  const [selectedSessionSummary, setSelectedSessionSummary] = useState<string>("");
  const [selectedSessionEvents, setSelectedSessionEvents] = useState<UpdateEvent[]>([]);
  const [sessionLoading, setSessionLoading] = useState(false);

  // User dropdown state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showBillingPanel, setShowBillingPanel] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Stripe Checkout returns to the dashboard with ?billing=success|cancel
  // (create_checkout_session's success/cancel URLs). Toast the outcome; on
  // success also open the Billing panel so the user sees their new plan, and
  // flag the Subscription section to refetch past the checkout→webhook race.
  const [checkoutJustSucceeded, setCheckoutJustSucceeded] = useState(false);
  useBillingReturn(() => {
    setCheckoutJustSucceeded(true);
    setShowBillingPanel(true);
  });

  // Consume the pre-OAuth tier stash (written in App before login). The toast
  // system has no action buttons, so the closest supported flow is: toast the
  // prompt and auto-open the Billing panel, which holds the Subscription
  // section (with the Subscribe buttons).
  useEffect(() => {
    let tier: string | null = null;
    try {
      tier = localStorage.getItem("affektra.pendingTier");
      if (tier) localStorage.removeItem("affektra.pendingTier");
    } catch (e) {
      console.error("[App] failed to read pending tier", e);
    }
    if (tier === "alap" || tier === "pro") {
      setShowBillingPanel(true);
      toast.info(t("settings.subResumePrompt", { tier: t(tier === "alap" ? "settings.planNameAlap" : "settings.planNamePro") }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Theme state — Reading Room (default, matches the landing) first. Palettes in tokens.css.
  const THEMES = [
    { id: "reading-room", label: "Reading Room", icon: "🖋️" },
    { id: "lavender", label: "Lavender", icon: "💜" },
    { id: "ink-night", label: "Ink Night", icon: "🌙" },
  ];

  const [theme, setTheme] = useState<string>(() => {
    try {
      return resolveTheme(localStorage.getItem("AFFEKTRA_THEME"));
    } catch {
      return resolveTheme(null);
    }
  });

  const cycleTheme = () => {
    const currentIdx = THEMES.findIndex((th) => th.id === theme);
    const newTheme = THEMES[(currentIdx + 1) % THEMES.length].id;
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    try {
      localStorage.setItem("AFFEKTRA_THEME", newTheme);
    } catch {}
  };

  const currentTheme = THEMES.find((th) => th.id === theme) || THEMES[0];

  // Language state
  const [language, setLang] = useState<string>(() => getCurrentLanguage());

  const cycleLanguage = () => {
    const currentIdx = LANGUAGES.findIndex(l => l.id === language);
    const nextIdx = (currentIdx + 1) % LANGUAGES.length;
    const newLang = LANGUAGES[nextIdx].id;
    setLang(newLang);
    setLanguage(newLang);
  };

  const currentLanguage = LANGUAGES.find(l => l.id === language) || LANGUAGES[0];

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    setSessionNote((prev) => (prev.patient === patient ? prev : { ...prev, patient }));
  }, [patient]);

  // Sync effective session id into sessionNote; clear stale summary on session change
  useEffect(() => {
    const sid = effectiveSessionId || "";
    setSessionNote((prev) => {
      if (prev.sessionId === sid) return prev;
      return { ...prev, sessionId: sid, summary: "" };
    });
  }, [effectiveSessionId]);

  // Sync fetched summary into sessionNote so canvas works even without SessionPanel open
  useEffect(() => {
    setSessionNote((prev) => (prev.summary === selectedSessionSummary ? prev : { ...prev, summary: selectedSessionSummary }));
  }, [selectedSessionSummary]);

  const pickString = useCallback((...vals: any[]) => {
    return vals.find((v) => typeof v === "string" && v.trim().length > 0) || "";
  }, []);

  const parseTranscriptToEvents = useCallback((text: string): UpdateEvent[] => {
    if (!text || !text.trim()) return [];
    const lines = text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    const events: UpdateEvent[] = [];
    let seq = 1;
    for (const line of lines) {
      const m = line.match(/^([^:]{1,80}):\s*(.+)$/);
      const speaker = (m?.[1] || "").trim() || "?";
      const content = (m?.[2] || line).trim();
      events.push({ seq, speaker, text: content });
      seq += 1;
    }
    return events;
  }, []);

  const extractEvents = useCallback(
    (data: any): UpdateEvent[] => {
      const chunkTurns: any[] = Array.isArray(data?.item?.chunks)
        ? data.item.chunks.flatMap((c: any) => (Array.isArray(c?.turns) ? c.turns : []))
        : [];
      const directTurns: any[] = (Array.isArray(data?.item?.turns) && data.item.turns) || (Array.isArray(data?.turns) && data.turns) || [];
      const turns = [...chunkTurns, ...directTurns];
      if (turns.length) {
        return turns.map((t: any, i: number) => ({
          seq: i + 1,
          speaker: typeof t?.speaker === "string" ? t.speaker : "?",
          text: typeof t?.text === "string" ? t.text : "",
          start_sec: typeof t?.start_sec === "number" ? t.start_sec : undefined,
          end_sec: typeof t?.end_sec === "number" ? t.end_sec : undefined,
        }));
      }

      const directEvents =
        (Array.isArray(data?.events) && data.events) ||
        (Array.isArray(data?.item?.events) && data.item.events) ||
        (Array.isArray(data?.session?.events) && data.session.events) ||
        [];
      if (directEvents.length) {
        return directEvents.map((e: any, i: number) => ({
          seq: typeof e.seq === "number" ? e.seq : i + 1,
          ts: typeof e.ts === "number" ? e.ts : undefined,
          start_sec: typeof e.start_sec === "number" ? e.start_sec : undefined,
          end_sec: typeof e.end_sec === "number" ? e.end_sec : undefined,
          speaker: typeof e.speaker === "string" ? e.speaker : "?",
          text: typeof e.text === "string" ? e.text : "",
        }));
      }

      // Prefer NDJSON raw_transcript (has per-turn timing) so replay duration is correct.
      // Plain `transcript` is a last-resort fallback for legacy sessions without raw_transcript.
      const rawNdjson = pickString(data?.raw_transcript, data?.item?.raw_transcript, data?.session?.raw_transcript);
      if (rawNdjson) {
        const parsed = parseNdjsonTranscript(rawNdjson);
        if (parsed.length) return parsed as UpdateEvent[];
      }
      const transcriptText = pickString(data?.transcript, data?.transcript_text, data?.item?.transcript, data?.session?.transcript);
      return parseTranscriptToEvents(transcriptText);
    },
    [parseTranscriptToEvents, pickString]
  );

  useEffect(() => {
    // Clear immediately on session change so SessionPanel's usePrefill can't inject
    // stale events from the previous session into the replay state while the new
    // session's data is still loading.
    setSelectedSessionSummary("");
    setSelectedSessionEvents([]);
    const run = async () => {
      if (!apiBaseUrl) return;
      if (!sessionId || sessionId === "current") {
        setSessionLoading(false);
        return;
      }
      setSessionLoading(true);
      try {
        const token = await getToken();
        const base = trimTrailingSlash(apiBaseUrl);
        const url = new URL(`${base}/sessions/${encodeURIComponent(sessionId)}`);
        if (patient) url.searchParams.set("patient_id", patient);

        const resp = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
        const text = await resp.text();
        let data: any;
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          data = { raw: text };
        }

        const fetchedSummary = pickString(
          data?.summary,
          data?.summary_text,
          data?.summary_md,
          data?.item?.summary,
          data?.item?.summary_text,
          data?.item?.summary_md,
          data?.session?.summary,
          data?.session?.summary_text,
          data?.session?.summary_md
        );
        setSelectedSessionSummary(fetchedSummary);

        const evs = extractEvents(data);
        const maxEndSec = evs.reduce((m, e: any) => typeof e?.end_sec === "number" && e.end_sec > m ? e.end_sec : m, 0);
        console.log("[DBG App.fetch] sessionId=", sessionId, "count=", evs.length, "maxEndSec=", maxEndSec, "firstEndSec=", (evs[0] as any)?.end_sec, "lastEndSec=", (evs[evs.length - 1] as any)?.end_sec, "rawTranscriptLen=", (data?.item?.raw_transcript || data?.raw_transcript || "").length);
        setSelectedSessionEvents(evs);
      } catch (err) {
        console.error("[DBG App.fetch] FAILED", err);
        setSelectedSessionSummary("");
        setSelectedSessionEvents([]);
      } finally {
        setSessionLoading(false);
      }
    };
    void run();
  }, [apiBaseUrl, sessionId, patient, getToken, extractEvents, pickString]);

  const AFFEKTRA_WIDTH_DEFAULT = 320;
  const SESSION_WIDTH_DEFAULT = 430;

  const MIN_PANEL_WIDTH = 200;
  const MAX_PANEL_WIDTH = 800;

  // Resizable panel widths
  const [sessionPanelWidth, setSessionPanelWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("SESSION_PANEL_WIDTH");
      return saved ? Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, parseInt(saved, 10))) : SESSION_WIDTH_DEFAULT;
    } catch {
      return SESSION_WIDTH_DEFAULT;
    }
  });

  const [affektraPanelWidth, setAffektraPanelWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("AFFEKTRA_PANEL_WIDTH");
      return saved ? Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, parseInt(saved, 10))) : AFFEKTRA_WIDTH_DEFAULT;
    } catch {
      return AFFEKTRA_WIDTH_DEFAULT;
    }
  });

  // Drag state for resizing
  const [draggingPanel, setDraggingPanel] = useState<"session" | "affektra" | null>(null);
  const dragStartX = useRef<number>(0);
  const dragStartWidth = useRef<number>(0);

  // Persist panel widths
  useEffect(() => {
    try {
      localStorage.setItem("SESSION_PANEL_WIDTH", String(sessionPanelWidth));
    } catch {}
  }, [sessionPanelWidth]);

  useEffect(() => {
    try {
      localStorage.setItem("AFFEKTRA_PANEL_WIDTH", String(affektraPanelWidth));
    } catch {}
  }, [affektraPanelWidth]);

  // Drag handlers for panel resizing
  const handleDragStart = useCallback((panel: "session" | "affektra", e: React.MouseEvent) => {
    e.preventDefault();
    setDraggingPanel(panel);
    dragStartX.current = e.clientX;
    dragStartWidth.current = panel === "session" ? sessionPanelWidth : affektraPanelWidth;
  }, [sessionPanelWidth, affektraPanelWidth]);

  useEffect(() => {
    if (!draggingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      // All panels drag from left edge - dragging left increases width
      const delta = dragStartX.current - e.clientX;
      const newWidth = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, dragStartWidth.current + delta));

      if (draggingPanel === "session") {
        setSessionPanelWidth(newWidth);
      } else {
        setAffektraPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setDraggingPanel(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [draggingPanel]);

  const [sessionVisible, setSessionVisible] = useState<boolean>(() => {
    try {
      return localStorage.getItem("SESSION_PANEL_VISIBLE") !== "0";
    } catch {
      return true;
    }
  });

  const [affektraOpen, setAffektraOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("AFFEKTRA_OPEN") === "1";
    } catch {
      return false;
    }
  });

  const [affektraCards, setAffektraCards] = useState<AgentCard[]>([]);
  const [affektraStatus, setAffektraStatus] = useState<AffektraRunStatus>("idle");
  const [affektraError, setAffektraError] = useState<string>("");

  const lastSeenKeyRef = useRef<string>("");
  const idleStreakRef = useRef<number>(0);
  const affektraAgentIntervalRef = useRef<number | null>(null);
  const affektraStatusRef = useRef<AffektraRunStatus>("idle");

  // Keep ref in sync with state (for use in callbacks without triggering re-renders)
  useEffect(() => {
    affektraStatusRef.current = affektraStatus;
  }, [affektraStatus]);

  const sessionPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("SESSION_PANEL_VISIBLE", sessionVisible ? "1" : "0");
    } catch {}
  }, [sessionVisible]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const isCmdOrCtrl = isMac ? (e as any).metaKey : e.ctrlKey;
      if (isCmdOrCtrl && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setSessionVisible((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("AFFEKTRA_OPEN", affektraOpen ? "1" : "0");
    } catch {}
  }, [affektraOpen]);

  const hasPatient = Boolean(patient && patient.trim().length > 0);

  // Unsaved-notes guard: switching session/patient wipes the panel's local edits,
  // so a dirty panel asks for confirmation first.
  const [pendingSwitch, setPendingSwitch] = useState<
    | { kind: "session"; id: string }
    | { kind: "patient"; id: string; name: string }
    | null
  >(null);

  const applySessionChange = useCallback((val: string) => {
    setStoryHighlight(null);
    setSessionId(val);
    if (!val || val === "current") setEffectiveSessionId(undefined);
    else setEffectiveSessionId(val);
  }, []);

  // ✅ accepts SessionSelector's (id, session?) signature; we only use id here
  const handleSessionChange = useCallback((val: string) => {
    if (isSessionDirty()) {
      setPendingSwitch({ kind: "session", id: val });
      return;
    }
    applySessionChange(val);
  }, [applySessionChange]);

  const applyPatientChange = useCallback((val: string, name?: string) => {
    setPatient(val);
    setPatientName(name || "");
  }, []);

  const handlePatientChange = useCallback((val: string, name?: string) => {
    if (isSessionDirty()) {
      setPendingSwitch({ kind: "patient", id: val, name: name || "" });
      return;
    }
    applyPatientChange(val, name);
  }, [applyPatientChange]);

  const confirmPendingSwitch = useCallback(() => {
    if (!pendingSwitch) return;
    clearSessionDirty();
    if (pendingSwitch.kind === "session") applySessionChange(pendingSwitch.id);
    else applyPatientChange(pendingSwitch.id, pendingSwitch.name);
    setPendingSwitch(null);
  }, [pendingSwitch, applySessionChange, applyPatientChange]);

  // ✅ keep selector in sync with minted effective sid (no jump back to "current").
  // When sid is undefined (session deleted), drop the selector back to "current"
  // so the selectedSessionEvents fetch re-runs and clears stale prefill data —
  // otherwise usePrefill in SessionPanel replays the deleted session's transcript.
  const handleEffectiveSessionId = useCallback((sid?: string) => {
    setEffectiveSessionId(sid);
    if (sid) {
      setSessionId((prev) => (prev === "current" || !prev ? sid : prev));
    } else {
      setSessionId("current");
    }
  }, []);

  const currentSessionForSiblings = effectiveSessionId || (sessionId !== "current" ? sessionId : "");

  // ============================================================
  // ✅ Poll outputs: POST /sessions/{session_id}/agent/outputs
  // ============================================================
  const fetchAffektraOutputs = useCallback(async () => {
    const base = trimTrailingSlash(apiBaseUrl || "");
    const sid = effectiveSessionId;

    if (!base || !sid) {
      setAffektraCards([]);
      setAffektraError("");
      setAffektraStatus("idle");
      lastSeenKeyRef.current = "";
      idleStreakRef.current = 0;
      return;
    }

    try {
      const token = await getToken();

      const resp = await fetch(`${base}/sessions/${encodeURIComponent(sid)}/agent/outputs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ session_id: sid, limit: 50, order: "desc" }),
      });

      const text = await resp.text();
      if (!resp.ok) throw new Error(text || `HTTP ${resp.status}`);

      const data = text ? JSON.parse(text) : {};
      const items: any[] = Array.isArray(data?.items) ? data.items : [];

      // Parse cards from response items
      const cards: AgentCard[] = [];
      items.forEach((it, i) => {
        const created = typeof it?.created_at === "string" ? it.created_at : "";

        // First check if cards are directly in the item (test mode generates them server-side)
        if (Array.isArray(it?.cards) && it.cards.length > 0) {
          it.cards.forEach((card: any, j: number) => {
            cards.push({
              id: card.id || `${created || i}_${j}`,
              type: card.type || "insight",
              title: card.title || `Card ${j + 1}`,
              body: card.body || "",
              action_data: card.action_data,
            });
          });
          return; // Successfully got cards from item
        }

        // Fallback: try to parse agent_output as JSON containing cards
        const rawOutput = it?.agent_output;
        if (typeof rawOutput === "string") {
          try {
            const parsed = JSON.parse(rawOutput);
            if (Array.isArray(parsed?.cards) && parsed.cards.length > 0) {
              parsed.cards.forEach((card: any, j: number) => {
                cards.push({
                  id: `${created || i}_${j}`,
                  type: card.type || "insight",
                  title: card.title || `Card ${j + 1}`,
                  body: card.body || "",
                  action_data: card.action_data,
                });
              });
              return; // Successfully parsed cards
            }
          } catch {
            // Not valid JSON, fall through to raw display
          }
        }

        // Last fallback: display raw output as insight card
        const body = typeof rawOutput === "string" ? rawOutput : JSON.stringify(it, null, 2);
        cards.push({
          id: created ? `${created}_${i}` : `out_${i}`,
          type: "insight",
          title: created ? `Output • ${created}` : `Output ${i + 1}`,
          body,
        });
      });

      // Only update cards if they've changed (prevents flickering)
      const newCardIds = cards.map(c => c.id).join(",");
      setAffektraCards(prev => {
        const prevCardIds = prev.map(c => c.id).join(",");
        if (prevCardIds === newCardIds) return prev;
        return cards;
      });

      // Worker failures write ERROR# marker rows; the list endpoint surfaces
      // the newest one (only when nothing succeeded since). Show it instead of
      // silently displaying no new cards.
      const latestError = (data as any)?.latest_error;
      if (latestError && typeof latestError.message === "string" && latestError.message) {
        setAffektraStatus("error");
        setAffektraError(String(latestError.message));
        return;
      }
      setAffektraError("");

      const newest = items[0];
      const newestKey =
        newest && (newest.created_at || newest.agent_output)
          ? `${String(newest.created_at || "")}|${String(newest.agent_output || "").slice(0, 80)}`
          : "";

      if (newestKey && newestKey !== lastSeenKeyRef.current) {
        lastSeenKeyRef.current = newestKey;
        idleStreakRef.current = 0;
        if (affektraStatusRef.current !== "running") setAffektraStatus("running");
      } else {
        idleStreakRef.current += 1;
        if (affektraStatusRef.current === "running" && idleStreakRef.current >= 6) {
          setAffektraStatus("idle");
        }
      }
    } catch (e: any) {
      setAffektraStatus("error");
      setAffektraError(e?.message || String(e));
    }
  }, [apiBaseUrl, effectiveSessionId, getToken]);

  useEffect(() => {
    if (!affektraOpen) return;

    let alive = true;
    const tick = async () => {
      if (!alive) return;
      await fetchAffektraOutputs();
    };

    void tick();
    const h = window.setInterval(() => void tick(), 15000);

    return () => {
      alive = false;
      window.clearInterval(h);
    };
  }, [affektraOpen, fetchAffektraOutputs]);

  useEffect(() => {
    if (affektraOpen) return;
    if (affektraStatus !== "running" && affektraStatus !== "stopping") return;

    const h = window.setInterval(() => void fetchAffektraOutputs(), 2500);
    return () => window.clearInterval(h);
  }, [affektraOpen, affektraStatus, fetchAffektraOutputs]);

  useEffect(() => {
    // Clear periodic agent interval when session changes
    if (affektraAgentIntervalRef.current !== null) {
      window.clearInterval(affektraAgentIntervalRef.current);
      affektraAgentIntervalRef.current = null;
    }

    setAffektraCards([]);
    setAffektraError("");
    setAffektraStatus("idle");
    lastSeenKeyRef.current = "";
    idleStreakRef.current = 0;

    if (affektraOpen && effectiveSessionId) {
      void fetchAffektraOutputs();
    }
  }, [effectiveSessionId, affektraOpen, fetchAffektraOutputs]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (affektraAgentIntervalRef.current !== null) {
        window.clearInterval(affektraAgentIntervalRef.current);
        affektraAgentIntervalRef.current = null;
      }
    };
  }, []);

  // ============================================================
  // ✅ REAL API CALL for Affektra actions
  // ============================================================
  const runAffektraAction = useCallback(
    async (ctx: AffektraActionCtx) => {
      const base = trimTrailingSlash(apiBaseUrl || "");
      if (!base) {
        console.error("[Affektra] apiBaseUrl missing");
        return;
      }

      const sid = ctx.sessionId || effectiveSessionId;
      const token = await getToken();

      if (ctx.actionId === "clear_outputs") {
        if (!sid) {
          console.warn("[Affektra] clear_outputs: missing session_id");
          return;
        }

        setAffektraCards([]);
        setAffektraError("");
        lastSeenKeyRef.current = "";
        idleStreakRef.current = 0;

        const url = `${base}/sessions/${encodeURIComponent(sid)}/agent/outputs/clear`;
        console.log("[Affektra] POST", url, { session_id: sid });

        const resp = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "content-type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ session_id: sid }),
        });

        const text = await resp.text();
        if (!resp.ok) {
          console.error("[Affektra] clear not OK", resp.status, text);
          setAffektraStatus("error");
          setAffektraError(text || `HTTP ${resp.status}`);
          return;
        }

        console.log("[Affektra] cleared OK", text);

        await fetchAffektraOutputs();
        setAffektraStatus("idle");
        return;
      }

      const payload = {
        card_id: ctx.cardId,
        action_id: ctx.actionId,
        patient_id: ctx.patientId,
        session_id: sid,
      };

      // Helper to call the agent tick endpoint
      const callAgentTick = async () => {
        const idToken = await ensureIdToken();
        const tickPayload: Record<string, any> = {
          card_id: "global",
          action_id: "play",
          patient_id: ctx.patientId,
          session_id: sid,
          id_token: idToken || "",
        };
        if (typeof ctx.maxChunkIndex === "number") {
          tickPayload.max_chunk_index = ctx.maxChunkIndex;
        }
        if (ctx.replay) {
          tickPayload.replay = true;
        }
        // NOTE: do not log tickPayload — it contains the id_token.
        console.log("[Affektra] Agent tick", `${base}/affektra/action`);
        try {
          const resp = await fetch(`${base}/affektra/action`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "content-type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(tickPayload),
          });
          const text = await resp.text();
          if (!resp.ok) {
            console.error("[Affektra] tick not OK", resp.status, text);
          } else {
            // NOTE: response body is AI clinical output (PHI) — log status only.
            console.log("[Affektra] tick OK", resp.status);
          }
          void fetchAffektraOutputs();
        } catch (err) {
          console.error("[Affektra] tick error", err);
        }
      };

      if (ctx.actionId === "play") {
        // Clear any existing interval
        if (affektraAgentIntervalRef.current !== null) {
          window.clearInterval(affektraAgentIntervalRef.current);
          affektraAgentIntervalRef.current = null;
        }

        setAffektraStatus("running");
        setAffektraError("");
        idleStreakRef.current = 0;

        // Call agent immediately
        console.log("[Affektra] Starting periodic agent (every 3 minutes)");
        void callAgentTick();

        // Start periodic interval (3 minutes = 180000ms)
        affektraAgentIntervalRef.current = window.setInterval(() => {
          console.log("[Affektra] Periodic agent tick");
          void callAgentTick();
        }, 180000);

        return;
      }

      if (ctx.actionId === "stop") {
        // Clear the periodic interval
        if (affektraAgentIntervalRef.current !== null) {
          console.log("[Affektra] Stopping periodic agent");
          window.clearInterval(affektraAgentIntervalRef.current);
          affektraAgentIntervalRef.current = null;
        }

        setAffektraStatus("idle");
        setAffektraError("");
        return;
      }

      // For other actions (not play/stop), use original behavior
      console.log("[Affektra] POST", `${base}/affektra/action`, payload);

      const resp = await fetch(`${base}/affektra/action`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const text = await resp.text();
      if (!resp.ok) {
        console.error("[Affektra] not OK", resp.status, text);
        setAffektraStatus("error");
        setAffektraError(text || `HTTP ${resp.status}`);
        return;
      }

      // NOTE: response body is AI clinical output (PHI) — log status only.
      console.log("[Affektra] OK", resp.status);

      void fetchAffektraOutputs();
    },
    [apiBaseUrl, getToken, fetchAffektraOutputs, effectiveSessionId]
  );

  // Submit card feedback (like/dislike) for model training
  const submitCardFeedback = useCallback(
    async (ctx: CardFeedbackCtx) => {
      const base = trimTrailingSlash(apiBaseUrl || "");
      if (!base) {
        console.error("[Affektra] apiBaseUrl missing for feedback");
        return;
      }

      const token = await getToken();
      if (!token) {
        console.error("[Affektra] Not authenticated for feedback");
        return;
      }

      const payload = {
        card_id: ctx.cardId,
        card_type: ctx.cardType,
        card_title: ctx.cardTitle,
        feedback: ctx.feedback,
        session_id: ctx.sessionId,
        patient_id: ctx.patientId,
      };

      console.log("[Affektra] Feedback", payload);

      try {
        const resp = await fetch(`${base}/affektra/feedback`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          const text = await resp.text();
          console.error("[Affektra] Feedback failed", resp.status, text);
        } else {
          console.log("[Affektra] Feedback submitted");
        }
      } catch (e) {
        console.error("[Affektra] Feedback error", e);
      }
    },
    [apiBaseUrl, getToken]
  );

  // Execute a typed agent card (analysis, add_board, add_goal)
  const executeAgentCard = useCallback(
    async (card: AgentCard): Promise<string | void> => {
      const token = await getToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // For analysis cards - run the prompt via Plans API
      if (card.type === "analysis") {
        const promptId = card.action_data?.prompt_id;
        if (!promptId) {
          throw new Error("No prompt_id in card action_data");
        }

        // Get session summary from current session note
        const summary = sessionNoteRef.current?.summary || "";

        const payload = {
          cluster_id: `affektra-${card.id}`,
          title: card.title,
          techniques: [card.action_data?.prompt_title || "Analysis"],
          pinned: [],
          template_ids: [promptId],
          system_instruction: "",
          variables: {},
          summary: summary,
        };

        // Async tick/poll via HTTP API v2
        const postUrl = `${API_BASE}/plans/runCluster?session=${encodeURIComponent(effectiveSessionId || "default")}`;
        const postResp = await fetch(postUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!postResp.ok) {
          const txt = await postResp.text();
          throw new Error(`Analysis failed: ${postResp.status} ${txt}`);
        }

        const { run_id } = await postResp.json();
        if (!run_id) throw new Error("No run_id returned");

        // Poll for result
        const pollStart = Date.now();
        const POLL_TIMEOUT = 120_000;
        const POLL_INTERVAL = 2000;
        let result: string | undefined;
        while (Date.now() - pollStart < POLL_TIMEOUT) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
          const pollUrl = `${API_BASE}/plans/runCluster?run_id=${encodeURIComponent(run_id)}`;
          const pollResp = await fetch(pollUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!pollResp.ok) throw new Error(`Poll error: ${pollResp.status}`);
          const pollData = await pollResp.json();
          if (pollData.status === "done") {
            result = pollData.result || pollData.output || JSON.stringify(pollData, null, 2);
            break;
          }
          if (pollData.status === "error") {
            // Entitlement failures arrive as 200 poll bodies, not HTTP 402.
            reportPlanRequiredRunError(pollData.error);
            throw new Error(pollData.error || "Worker error");
          }
        }
        if (result === undefined) throw new Error("Timeout waiting for analysis result");
        return result;
      }

      // For add_board cards - create a new board
      if (card.type === "add_board") {
        const boardTitle = card.action_data?.board_title || card.title || "New Board";
        const boardDesc = card.action_data?.board_desc || card.body || "";

        const resp = await fetch(`${API_BASE}/boards`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: boardTitle,
            description: boardDesc,
            lanes: [],
          }),
        });

        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Failed to create board: ${resp.status} ${txt}`);
        }

        await resp.json();
        return `Board "${boardTitle}" created successfully!`;
      }

      // For add_goal cards - add a goal to the first board
      if (card.type === "add_goal") {
        const goalTitle = card.action_data?.goal_title || card.title || "New Goal";
        const goalDesc = card.action_data?.goal_desc || card.body || "";

        // First, get the existing board
        const listResp = await fetch(`${API_BASE}/boards`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!listResp.ok) {
          throw new Error("Failed to fetch boards");
        }

        const listData = await listResp.json();
        let board = listData.items?.[0];

        // Create a board if none exists
        if (!board) {
          const createResp = await fetch(`${API_BASE}/boards`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              title: "Goals",
              description: "Therapy goals and tasks",
              lanes: [],
            }),
          });
          if (!createResp.ok) throw new Error("Failed to create board");
          const createData = await createResp.json();
          board = createData.board;
        }

        // Add a new lane (goal) to the board
        const newLane = {
          id: crypto.randomUUID?.() || Math.random().toString(36).slice(2, 10),
          title: goalTitle,
          desc: goalDesc,
          createdAt: Date.now(),
          tasks: [],
          completed: [],
        };

        const lanes = Array.isArray(board.lanes) ? [...board.lanes, newLane] : [newLane];

        const updateResp = await fetch(`${API_BASE}/boards/${encodeURIComponent(board.id)}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ lanes }),
        });

        if (!updateResp.ok) {
          const txt = await updateResp.text();
          throw new Error(`Failed to add goal: ${updateResp.status} ${txt}`);
        }

        return `Goal "${goalTitle}" added to board!`;
      }

      // For insight cards - just return the body (no action needed)
      return card.body || "No additional content";
    },
    [getToken, effectiveSessionId]
  );

  // right-rail width: single unified session panel (insights live inside it)
  const rightRailWidth = sessionVisible ? sessionPanelWidth : 0;

  return (
    <div className="appContainer">
      {sessionLoading && (<>
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3, zIndex: 9999,
          background: "var(--color-accent)",
          animation: "loadingBar 1.2s ease-in-out infinite",
        }} />
        <div style={{ position: "absolute", inset: 0, zIndex: 9998, cursor: "wait" }} />
      </>)}
      {/* Sidebar */}
      <nav className="navRail" aria-label="Primary navigation">
        <div className="navWordmark">
          <span className="navWordmark__full">Affektra <em>studio</em></span>
          <span className="navWordmark__mini" aria-hidden>A</span>
        </div>
        {NAV_GROUP_LABELS.map((groupLabelKey, groupIdx) => {
          const items = visibleNav.filter((ni) => ni.group === groupIdx);
          if (items.length === 0) return null;
          return (
            <React.Fragment key={groupIdx}>
              <div className="navSep--labeled" aria-hidden="true">
                <span className="navSepChip">{t(groupLabelKey)}</span>
              </div>
              <div className="navGroup">
                {items.map((ni) => {
                  const Icon = ni.icon;
                  return (
                    <button
                      key={ni.tab}
                      onClick={() => setActiveTab(ni.tab)}
                      onContextMenu={(e) => {
                        if (!ni.prefId) return;
                        e.preventDefault();
                        setCtxMenu({ x: e.clientX, y: e.clientY, tabId: ni.prefId });
                      }}
                      className={`navItem ${activeTab === ni.tab ? "is-active" : ""}`}
                      aria-label={t(ni.labelKey)}
                      title={t(ni.labelKey)}
                    >
                      <span className="navIcon" aria-hidden>
                        <Icon className="navIconSvg" />
                      </span>
                      <span className="navLabel">{t(ni.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Nav context menu */}
      {ctxMenu && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: ctxMenu.y,
            left: ctxMenu.x,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            minWidth: 160,
            overflow: "hidden",
          }}
        >
          <button
            onClick={() => {
              hideTab(ctxMenu.tabId);
              setCtxMenu(null);
            }}
            style={{
              display: "block",
              width: "100%",
              padding: "10px 14px",
              border: "none",
              background: "transparent",
              fontSize: 13,
              color: "var(--color-ink)",
              cursor: "pointer",
              textAlign: "left",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {t("settings.hideFromToolbar")}
          </button>
        </div>
      )}

      {/* Main column + header */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0  }}>
        <div className="headerBar headerBar--between">
          <div className="headerLeft">
            <PatientSelector selected={patient} onChange={handlePatientChange} apiBaseUrl={apiBaseUrl} getToken={getToken} />

            <SessionSelector
              selected={sessionId}
              onChange={(id: string) => handleSessionChange(id)}
              apiBaseUrl={apiBaseUrl}
              getToken={getToken}
              patientId={patient || undefined}
            />

            <ConfirmDialog
              open={pendingSwitch !== null}
              title={t("session.unsavedTitle")}
              message={t("session.unsavedSwitchMessage")}
              confirmLabel={t("session.discard")}
              variant="danger"
              onConfirm={confirmPendingSwitch}
              onCancel={() => {
                setPendingSwitch(null);
                setStoryHighlight(null);
              }}
            />
          </div>

          {/* Right side: recording status, session toggle, user dropdown */}
          <div className="headerRight" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <RecordingStatusStrip onClick={() => setSessionVisible(true)} />
            <button
              onClick={() => setSessionVisible((v) => !v)}
              className="headerBtn"
              title="Toggle session panel (Ctrl/Cmd + J)"
              aria-label="Toggle session panel"
            >
              <span className="headerBtn__icon" aria-hidden>
                <IconPanelRight width={16} height={16} />
              </span>
            </button>

            <NotesToReview
              onOpen={(pid, name, sid) => {
                handlePatientChange(pid, name);
                handleSessionChange(sid);
                setSessionVisible(true);
              }}
            />

            {/* User dropdown */}
            <div ref={userMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="headerBtn"
                title={userEmail || "User menu"}
                aria-label="User menu"
                aria-expanded={userMenuOpen}
                onClick={() => setUserMenuOpen((v) => !v)}
              >
                <span className="headerBtn__icon" aria-hidden>
                  <IconUser width={16} height={16} />
                </span>
              </button>

              {userMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    minWidth: 200,
                    zIndex: 1000,
                    overflow: "hidden",
                  }}
                >
                  {/* User email header */}
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
                    <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 2 }}>Signed in as</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-title)", wordBreak: "break-all" }}>
                      {userEmail || "User"}
                    </div>
                  </div>

                  {/* Menu items */}
                  <div style={{ padding: "8px 0" }}>
                    {/* Patients */}
                    <button
                      onClick={() => { setActiveTab("patients"); setUserMenuOpen(false); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: activeTab === "patients" ? "var(--color-bg)" : "transparent",
                        fontSize: 14,
                        color: "var(--color-ink)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = activeTab === "patients" ? "var(--color-bg)" : "transparent")}
                    >
                      <IconUser width={16} height={16} />
                      {t("nav.patients")}
                    </button>

                    {/* Calendar */}
                    <button
                      onClick={() => { setActiveTab("calendar"); setUserMenuOpen(false); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: activeTab === "calendar" ? "var(--color-bg)" : "transparent",
                        fontSize: 14,
                        color: "var(--color-ink)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = activeTab === "calendar" ? "var(--color-bg)" : "transparent")}
                    >
                      <IconCalendar width={16} height={16} />
                      {t("tabs.calendar")}
                    </button>

                    {/* Theme toggle */}
                    <button
                      onClick={() => { cycleTheme(); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: "transparent",
                        fontSize: 14,
                        color: "var(--color-ink)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>{currentTheme.icon}</span>
                        {t("nav.theme")}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                        {currentTheme.label}
                      </span>
                    </button>


                    {/* Language toggle */}
                    <button
                      onClick={() => { cycleLanguage(); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: "transparent",
                        fontSize: 14,
                        color: "var(--color-ink)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>{currentLanguage.icon}</span>
                        {t("nav.language")}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--color-muted)" }}>
                        {currentLanguage.label}
                      </span>
                    </button>

                    <button
                      onClick={() => { setShowBillingPanel(true); setUserMenuOpen(false); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: "transparent",
                        fontSize: 14,
                        color: "var(--color-ink)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ fontSize: 16 }}>$</span>
                      {t("nav.billing")}
                    </button>

                    <button
                      onClick={() => { setShowSettingsPanel(true); setUserMenuOpen(false); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: "transparent",
                        fontSize: 14,
                        color: "var(--color-ink)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ fontSize: 16 }}>&#x2699;</span>
                      {t("nav.settings")}
                    </button>
                  </div>

                  {/* Sign out */}
                  <div style={{ borderTop: "1px solid var(--color-border)", padding: "8px 0" }}>
                    <button
                      onClick={() => { setUserMenuOpen(false); onLogout(); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "10px 16px",
                        border: "none",
                        background: "transparent",
                        fontSize: 14,
                        color: "var(--color-danger)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-danger-ghost)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <IconLogout width={16} height={16} />
                      {t("nav.logout")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="contentRow" style={{ position: "relative", flex: 1, display: "flex", overflow: "hidden", gap: 0 }}>
          {/* MAIN WORKSPACE */}
          {/* Reserve the right rail's width when it's open: the panel is
              position:absolute, so without this the workspace extends under it
              and the panel intercepts clicks on right-edge controls. */}
          <div style={{ flex: 1, padding: 12, overflow: "auto", background: "var(--color-bg)", marginRight: sessionVisible ? rightRailWidth : 0 }}>
            {activeTab === "patients" && <PatientsPanel onLoadPatient={(id, name) => { setPatient(id); setPatientName(name || ""); setActiveTab("prepare"); }} />}
            {activeTab === "overview" && <PatientDashboard patient={patient} patientName={patientName} />}
            {activeTab === "prepare" && <PreparePanel patient={patient} />}
            {activeTab === "similar" && (
              <StoryPanel
                patientId={patient}
                currentSessionId={currentSessionForSiblings || undefined}
                onOpenSession={(sessionId, opts) => {
                  // dirty-session guard: unsaved note edits must trigger the pendingSwitch confirm
                  handleSessionChange(sessionId);
                  setStoryHighlight(
                    opts?.highlightMs
                      ? { sessionId, startMs: opts.highlightMs[0], endMs: opts.highlightMs[1] }
                      : null,
                  );
                  setSessionVisible(true); // rail is collapsible; reveal it or the deep link silently no-ops
                  // stay on the Histories tab: the session rail is visible on any tab,
                  // and switching tabs would unmount StoryPanel and lose braid state
                }}
              />
            )}

            {activeTab === "tech" && (
              <ToolkitPage
                patientId={patient}
                sessionId={effectiveSessionId || undefined}
                language={(language === "hu" ? "hu" : "en") as "en" | "hu"}
                apiBaseUrl={apiBaseUrl}
                getToken={getToken}
              />
            )}

            {activeTab === "prompts" && (
              <Prompts
                selectedSessionId={sessionId}
                patientId={patient || undefined}
                summarizePromptId={prefs.summarizePrompt?.id ?? null}
                onSelectSummarizePrompt={setSummarizePrompt}
              />
            )}

            {activeTab === "assistant" &&
              (hasPatient ? (
                <AssistantPanel patientId={patient} />
              ) : (
                <SelectPatientEmptyState title="Assistant needs a patient" hint="Pick a patient above to start (we keep chat history per patient)." />
              ))}

            {activeTab === "assessments" &&
              (hasPatient ? (
                <AssessmentsPanel patientId={patient} />
              ) : (
                <SelectPatientEmptyState title="Assessments need a patient" hint="Select a patient above to view and run assessments." />
              ))}

            {activeTab === "calendar" && <CalendarPage />}
            {activeTab === "goals" &&
              (hasPatient ? (
                <GoalsBoard patientId={patient} />
              ) : (
                <SelectPatientEmptyState title="Goals need a patient" hint="Select a patient above to manage therapy goals." />
              ))}

            {activeTab === "library" && <LibraryPanel />}
          </div>

          {/* RIGHT RAIL (✅ now actually hides/shows) */}
          {sessionVisible && <div className="column-divider" />}
          <div
            className="sessionPanel"
            style={{
              width: `${rightRailWidth}px`,
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              overflow: "hidden",
              // display:none when collapsed — otherwise `.sessionPanel { min-width: 200px }`
              // (and, on ≤1100px screens, its opaque surface bg + border + shadow) leaves a
              // visible 200px residual strip even though the inline width is 0.
              display: sessionVisible ? "flex" : "none",
              flexDirection: "row",
              pointerEvents: sessionVisible ? "auto" : "none",
              zIndex: 10,
            }}
          >
            {sessionVisible && (
              <div style={{ display: "flex", flex: 1, minWidth: 0, height: "100%", overflow: "hidden", gap: 0, padding: "8px 8px 8px 0", background: "var(--color-bg)" }}>
                {/* Invisible drag handle on session panel left edge */}
                <div
                  onMouseDown={(e) => handleDragStart("session", e)}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: 6,
                    height: "100%",
                    cursor: "col-resize",
                    zIndex: 10,
                  }}
                />
                <div
                  ref={sessionPanelRef}
                  style={{
                    flex: "0 0 auto",
                    width: `${sessionPanelWidth}px`,
                    minWidth: `${sessionPanelWidth}px`,
                    maxWidth: `${sessionPanelWidth}px`,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <SessionPanel
                    apiBaseUrl={apiBaseUrl}
                    getToken={getToken}
                    selectedSessionId={sessionId}
                    selectedPatientId={patient}
                    summarizePrompt={prefs.summarizePrompt ?? null}
                    prefillSummary={selectedSessionSummary}
                    prefillEvents={selectedSessionEvents}
                    highlightRange={
                      storyHighlight && storyHighlight.sessionId === (effectiveSessionId || sessionId)
                        ? { startMs: storyHighlight.startMs, endMs: storyHighlight.endMs }
                        : null
                    }
                    onSummary={(s) => setSessionNote((prev) => ({ ...prev, summary: s }))}
                    affektraOpen={affektraOpen}
                    onAffektraOpenChange={setAffektraOpen}
                    onEffectiveSessionId={handleEffectiveSessionId}
                    onRunAffektraAction={runAffektraAction}
                    affektraCards={affektraCards}
                    affektraStatus={affektraStatus}
                    affektraError={affektraError}
                    onExecuteCard={executeAgentCard}
                    onCardFeedback={submitCardFeedback}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Billing Panel Modal */}
      {showBillingPanel && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 2000,
            }}
            onClick={() => setShowBillingPanel(false)}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "var(--color-surface)",
              borderRadius: 6,
              boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
              border: "1px solid var(--color-border)",
              width: "min(600px, 90vw)",
              maxHeight: "80vh",
              overflow: "auto",
              zIndex: 2001,
            }}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t("nav.billing")}</h2>
              <button
                onClick={() => setShowBillingPanel(false)}
                style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "var(--color-muted)" }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 20 }}>
              <SubscriptionSection refetchAfterCheckout={checkoutJustSucceeded} />
              {/* Internal per-function LLM cost/usage stats — admin debugging
                  only, never exposed to therapists. Opt in on an admin browser
                  via localStorage.setItem("affektra.adminUsage", "1"). */}
              {localStorage.getItem("affektra.adminUsage") === "1" && (
                <BillingContent apiBaseUrl={apiBaseUrl} getToken={getToken} />
              )}
            </div>
          </div>
        </>
      )}

      {/* Settings Panel Modal */}
      {showSettingsPanel && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 2000,
            }}
            onClick={() => setShowSettingsPanel(false)}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "var(--color-surface)",
              borderRadius: 6,
              boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
              border: "1px solid var(--color-border)",
              width: "min(500px, 90vw)",
              maxHeight: "80vh",
              overflow: "auto",
              zIndex: 2001,
            }}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Settings</h2>
              <button
                onClick={() => setShowSettingsPanel(false)}
                style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "var(--color-muted)" }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: 16 }}>
              <SettingsContent
                visibleTabs={prefs.visibleTabs}
                onToggleTab={toggleTab}
                autoNoteFormat={prefs.autoNoteFormat}
                onAutoNoteFormat={(f) => updatePrefs({ autoNoteFormat: f })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* Billing Content Component */
interface BillingFunction {
  function_name: string;
  total_cost_usd: number;
  total_tokens: number;
  call_count: number;
}

const BillingContent: React.FC<{ apiBaseUrl: string; getToken: () => Promise<string> }> = ({ apiBaseUrl, getToken }) => {
  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState<BillingFunction[]>([]);
  const [totalCost, setTotalCost] = useState(0);
  const [totalCalls, setTotalCalls] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const r = await fetch(`${apiBaseUrl}/billing/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setFunctions(data.by_function || []);
        setTotalCost(data.summary?.total_cost_usd ?? 0);
        setTotalCalls(data.summary?.total_calls ?? 0);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBaseUrl, getToken]);

  if (loading) return <div style={{ color: "var(--color-muted)" }}>Loading billing data...</div>;
  if (error) return <div style={{ color: "var(--color-danger)" }}>Error: {error}</div>;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={{ padding: 16, background: "var(--color-brand-ghost)", borderRadius: 12, border: "1px solid color-mix(in srgb, var(--color-brand) 35%, transparent)" }}>
          <div style={{ fontSize: 12, color: "var(--color-brand)", fontWeight: 600, marginBottom: 4 }}>Total OpenAI Cost</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--color-title)" }}>${(totalCost || 0).toFixed(2)}</div>
        </div>
        <div style={{ padding: 16, background: "var(--color-subsurface)", borderRadius: 12, border: "1px solid var(--color-border)" }}>
          <div style={{ fontSize: 12, color: "var(--color-muted)", fontWeight: 600, marginBottom: 4 }}>Total API Calls</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--color-title)" }}>{(totalCalls || 0).toLocaleString()}</div>
        </div>
      </div>

      {functions.length === 0 ? (
        <div style={{ color: "var(--color-muted)", textAlign: "center", padding: 24 }}>No usage data yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-ink)", marginBottom: 4 }}>Cost by Function</div>
          {functions.map((fn) => (
            <div key={fn.function_name} style={{ padding: 12, border: "1px solid var(--color-border)", borderRadius: 10, background: "var(--color-subsurface)" }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{fn.function_name}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 13 }}>
                <div>
                  <span style={{ color: "var(--color-muted)" }}>Cost:</span> <strong>${(fn.total_cost_usd || 0).toFixed(4)}</strong>
                </div>
                <div>
                  <span style={{ color: "var(--color-muted)" }}>Tokens:</span> <strong>{(fn.total_tokens || 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span style={{ color: "var(--color-muted)" }}>Calls:</span> <strong>{fn.call_count || 0}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Settings Content Component */
const SettingsContent: React.FC<{
  visibleTabs: TabId[];
  onToggleTab: (id: TabId) => void;
  autoNoteFormat?: AutoNoteFormat;
  onAutoNoteFormat: (f: AutoNoteFormat) => void;
}> = ({ visibleTabs, onToggleTab, autoNoteFormat, onAutoNoteFormat }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { ensureToken } = useAuth();
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE" || deletingAccount) return;
    if (!window.confirm(t("settings.deleteAccountConfirmPrompt"))) return;
    setDeletingAccount(true);
    try {
      const token = await ensureToken();
      const r = await fetch(`${API_BASE}/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: "DELETE" }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      logout();
    } catch (e) {
      console.error("[settings] account deletion failed", e);
      toast.error(t("settings.deleteAccountFailed"));
      setDeletingAccount(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Toolbar section */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" }}>{t("settings.toolbar")}</div>
        <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 10 }}>{t("settings.toolbarHint")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {NAV_ITEMS.filter((ni): ni is NavItem & { prefId: TabId } => !!ni.prefId).map((ni) => {
            const isChecked = visibleTabs.includes(ni.prefId);
            const disabled = isChecked && visibleTabs.length <= 2;
            return (
              <label
                key={ni.prefId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: disabled ? "not-allowed" : "pointer",
                  padding: "6px 8px",
                  borderRadius: 4,
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={disabled}
                  onChange={() => onToggleTab(ni.prefId)}
                />
                <span style={{ fontSize: 13, color: "var(--color-ink)" }}>{t(ni.labelKey)}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" }}>Notifications</div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" defaultChecked />
          <span style={{ fontSize: 13, color: "var(--color-ink)" }}>Email notifications for session summaries</span>
        </label>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-title)" }}>Data Export</div>
        <button
          style={{
            padding: "8px 14px",
            background: "var(--color-bg)",
            border: "1px solid var(--color-border)",
            borderRadius: 4,
            fontSize: 13,
            cursor: "pointer",
            color: "var(--color-ink)",
          }}
          onClick={() => toast.info("Export functionality coming soon")}
        >
          Export All Data
        </button>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "var(--color-title)" }}>{t("settings.autoNote.title")}</div>
        <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 8 }}>{t("settings.autoNote.help")}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(["soap", "dap", "birp", "off"] as AutoNoteFormat[]).map((f) => (
            <button
              key={f}
              onClick={() => onAutoNoteFormat(f)}
              aria-pressed={autoNoteFormat === f}
              style={{
                padding: "6px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                border: `1px solid ${autoNoteFormat === f ? "var(--color-brand)" : "var(--color-border)"}`,
                background: autoNoteFormat === f ? "var(--color-brand)" : "var(--color-bg)",
                color: autoNoteFormat === f ? "#fff" : "var(--color-ink)",
              }}
            >
              {f === "off" ? t("settings.autoNote.off") : f.toUpperCase()}
            </button>
          ))}
        </div>
        {!autoNoteFormat && (
          <div style={{ fontSize: 12, color: "var(--color-warning)", marginTop: 6 }}>{t("settings.autoNote.notSet")}</div>
        )}
      </div>

      <PasskeysSection />

      <div style={{ paddingTop: 16, borderTop: "1px solid var(--color-danger)" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--color-danger)" }}>
          {t("settings.dangerZone")}
        </div>
        <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 10 }}>
          {t("settings.deleteAccountWarning")}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={t("settings.deleteAccountType")}
            style={{
              flex: 1,
              padding: "8px 10px",
              border: "1px solid var(--color-border)",
              borderRadius: 4,
              fontSize: 13,
            }}
          />
          <button
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== "DELETE" || deletingAccount}
            style={{
              padding: "8px 14px",
              background: deleteConfirmText === "DELETE" ? "var(--color-danger)" : "var(--color-bg)",
              color: deleteConfirmText === "DELETE" ? "#fff" : "var(--color-muted)",
              border: "1px solid var(--color-danger)",
              borderRadius: 4,
              fontSize: 13,
              cursor: deleteConfirmText === "DELETE" && !deletingAccount ? "pointer" : "not-allowed",
            }}
          >
            {deletingAccount ? t("settings.deleting") : t("settings.deleteAccount")}
          </button>
        </div>
      </div>

      <div style={{ paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
        <div style={{ fontSize: 12, color: "var(--color-muted)" }}>
          Affektra v1.0
        </div>
      </div>
    </div>
  );
};

export { AuthedApp };
